import React, { useEffect, useMemo, useState } from "react";
import { getSkill } from "../../../lib/inventorySkills";
import { useSeasonStats } from "../../../lib/seasonStats";
import { computeShooting, secList, useShootingSection } from "../../../lib/shooting";
import { ShootingStatsPanel } from "./ShootingStatsPanel";
import { ShootingOutcomes } from "./ShootingOutcomes";
import { PlanLink } from "../PlanLink";
import { ItemRow } from "../ItemRow";

export const EMPTY_SHOOTING = { outcome_tags: [], shot_type_ranks: {}, flags: [], flagged_mechanics: [], checkpoints: [], limiting_factors: [], phase: null };

// Shooting category = same 4 sections as Finishing: stats panel → rate the interactions → where it breaks down + tags → the plan.
export function ShootingCategory({ playerId, items, byId, shooting, onShootingChange, rowProps, readOnly }) {
  const lib = useShootingSection();
  const { records } = useSeasonStats(playerId);
  const sh = shooting || EMPTY_SHOOTING;
  const [season, setSeason] = useState(sh.season || null);
  useEffect(() => { if (!season && records.length) setSeason(sh.season && records.some(r => r.season === sh.season) ? sh.season : records[0].season); }, [records, season, sh.season]);
  const rec = records.find(r => r.season === season);
  const result = useMemo(() => computeShooting(rec?.stats), [rec]);
  useEffect(() => {
    if (readOnly || !season) return;
    const flags = result?.flags || [];
    if (season !== sh.season || JSON.stringify(flags) !== JSON.stringify(sh.flags || [])) onShootingChange(s => ({ ...(s || EMPTY_SHOOTING), season, flags }));
  }, [season, result]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!lib) return <div className="text-[10px] text-[#757575]">Loading Shooting…</div>;
  const order = secList(lib, "Interaction").map(r => r.id);
  const ordered = [...items].sort((a, b) => (order.indexOf(a.id) + 1 || 99) - (order.indexOf(b.id) + 1 || 99));
  const linked = secList(lib, "Linked Skill").map(r => getSkill(r.id)).filter(s => s && s.status === "active");
  const look = new Set((result?.flags || []).flatMap(f => secList(lib, "Stat Flag").find(x => x.id === f)?.points_to || []));
  const row = (s, fromCat) => (
    <div key={s.id} className="relative" data-testid={`sh-skill-${s.id}`}>
      {(look.has(s.id) || fromCat) && <div className="absolute right-3 -top-2 z-10 flex items-center gap-1.5">
        {look.has(s.id) && <span className="px-1.5 py-0.5 bg-[#f59e0b] text-[#121212] text-[8px] uppercase tracking-[0.2em] font-bold" data-testid={`sh-look-${s.id}`}>Look at this</span>}
        {fromCat && <span className="px-1.5 py-0.5 border border-white/20 bg-[#1c1c1c] text-[#a0a0a0] text-[8px] uppercase tracking-[0.2em]" data-testid={`sh-linked-${s.id}`}>From {fromCat}</span>}</div>}
      <ItemRow skill={s} rating={byId[s.id]} readOnly={readOnly} {...rowProps(s)} />
    </div>
  );
  return (
    <div className="space-y-3" data-testid="sh-category">
      <ShootingStatsPanel playerId={playerId} lib={lib} records={records} season={season} setSeason={setSeason} result={result} />
      <div className="border border-white/10 bg-[#141414] p-4 space-y-2" data-testid="sh-ratings">
        <div className="flex items-center gap-3 flex-wrap"><span className="text-[9px] font-mono text-[var(--org-accent)]">2</span><div className="text-[10px] uppercase tracking-[0.35em] font-bold text-white">Rate the interactions</div><div className="text-[10px] text-[#757575]">Anchor / Good / Rate Limiter · the stats inform, the coach rates</div></div>
        <div className="space-y-3 pt-1">{ordered.map(s => row(s, null))}{linked.map(s => row(s, "Tactical"))}</div>
      </div>
      <ShootingOutcomes lib={lib} sh={sh} byId={byId} result={result} readOnly={readOnly} onChange={onShootingChange} />
      <PlanLink playerId={playerId} sectionKey="shooting" />
    </div>
  );
}
